import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db74d6baskivmpnrcfog';

const contactItems = [
  { icon: 'ri-mail-line', label: '邮箱', value: 'hello@online-platform.cn', note: '工作日 24 小时内回复' },
  { icon: 'ri-phone-line', label: '电话', value: '400-800-2026', note: '周一至周五 9:00 - 18:00' },
  { icon: 'ri-map-pin-line', label: '服务范围', value: '全国 · 线上为主', note: '部分服务支持线下到店' },
  { icon: 'ri-customer-service-2-line', label: '合作咨询', value: 'bd@online-platform.cn', note: '内容合作与商务对接' },
];

export default function Contact() {
  useSeo({
    title: '联系反馈 - 在线服务平台',
    description: '通过在线平台联系我们，提交使用反馈与建议。我们认真对待每一条留言，持续优化平台体验。',
    keywords: '联系在线,在线反馈,在线平台联系,意见反馈',
  });

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('contact_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      formEl.reset();
      return;
    }
    formData.delete('contact_alt');

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSuccess = response.ok && parsed?.code === 'OK';
      const isSpam = typeof serverMsg === 'string' && serverMsg.includes('spam');

      if (isSuccess && !isSpam) {
        setStatus('success');
        setFormError('');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(String(serverMsg || '提交失败，请稍后重试'));
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试');
    }
  };

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-12">
      <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
        <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">联系反馈</span>
      </nav>

      <header className="mb-8 max-w-3xl">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">联系与反馈</h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
          无论你是想反馈使用问题、提出改进建议，还是寻求内容合作，都欢迎通过以下方式联系我们。我们会认真阅读每一条留言，并持续优化平台体验。
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        <div className="lg:col-span-1 space-y-3">
          {contactItems.map((item) => (
            <div key={item.label} className="flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200">
              <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 text-xl">
                <i className={item.icon}></i>
              </span>
              <div className="min-w-0">
                <p className="text-xs text-foreground-500">{item.label}</p>
                <p className="text-sm font-semibold text-foreground-900 mt-0.5 break-all">{item.value}</p>
                <p className="text-xs text-foreground-500 mt-1">{item.note}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-2">
          <div className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-7">
            <h2 className="text-lg font-bold text-foreground-950 mb-1">提交反馈</h2>
            <p className="text-sm text-foreground-500 mb-6">带 <span className="text-accent-600">*</span> 的为必填项，反馈内容请控制在 500 字以内。</p>

            <form data-readdy-form id="online-feedback-form" onSubmit={handleSubmit} className="space-y-5">
              <input
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="feedback-extra-field"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm text-foreground-700">姓名 <span className="text-accent-600">*</span></span>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="请输入您的称呼"
                    className="w-full px-3.5 py-2.5 text-sm rounded-md bg-background-50 border border-background-200 focus:border-primary-400 focus:outline-none text-foreground-900 placeholder:text-foreground-400"
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm text-foreground-700">联系方式（邮箱） <span className="text-accent-600">*</span></span>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="例如 name@example.com"
                    className="w-full px-3.5 py-2.5 text-sm rounded-md bg-background-50 border border-background-200 focus:border-primary-400 focus:outline-none text-foreground-900 placeholder:text-foreground-400"
                  />
                </label>
              </div>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm text-foreground-700">反馈内容 <span className="text-accent-600">*</span></span>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  placeholder="请描述你遇到的问题或建议（不超过 500 字）"
                  className="w-full px-3.5 py-2.5 text-sm rounded-md bg-background-50 border border-background-200 focus:border-primary-400 focus:outline-none text-foreground-900 placeholder:text-foreground-400 resize-y"
                ></textarea>
              </label>

              {status === 'success' && (
                <div className="flex items-start gap-3 p-4 rounded-md bg-primary-50 border border-primary-100">
                  <i className="ri-checkbox-circle-line text-primary-600 text-xl"></i>
                  <p className="text-sm text-primary-700">提交成功，感谢你的反馈！我们会尽快查看并处理。</p>
                </div>
              )}
              {status === 'error' && (
                <div className="flex items-start gap-3 p-4 rounded-md bg-accent-50 border border-accent-200">
                  <i className="ri-error-warning-line text-accent-700 text-xl"></i>
                  <p className="text-sm text-accent-800">{formError || '提交失败，请稍后重试'}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    提交反馈
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}